import React from 'react';
import SVGA from 'svgaplayerweb';

/**
 * Ô xem trước (ảnh hoặc hiệu ứng SVGA) của 1 item shop — dùng chung cho 6 trang.
 *
 * - File .svga: CHỈ tạo player khi ô lọt vào viewport (IntersectionObserver)
 *   -> không tạo hàng nghìn player cùng lúc (nguyên nhân chính gây lag).
 * - File ảnh: render <img loading="lazy"> do React quản lý
 *   (KHÔNG dùng jQuery replaceWith nên không phá cây DOM của React).
 * - url rỗng/không hợp lệ: không render gì -> không làm sập bảng.
 */
class ShopPreview extends React.PureComponent {
    constructor(props) {
        super(props);
        this.previewRef = React.createRef();
        this.player = null;
        this.observer = null;
    }

    componentDidMount() {
        this.watchPreview();
    }

    componentDidUpdate(prevProps) {
        const prevUrl = (prevProps && prevProps.url) || '';
        const nextUrl = this.props.url || '';
        if (prevUrl !== nextUrl) {
            this.destroyPlayer();
            this.unwatchPreview();
            this.watchPreview();
        }
    }

    componentWillUnmount() {
        this.unwatchPreview();
        this.destroyPlayer();
    }

    /** Chỉ theo dõi khi nào ô vào viewport thì mới tạo player SVGA. */
    watchPreview() {
        const el = this.previewRef.current;
        if (!el) return;

        // Trình duyệt không hỗ trợ IntersectionObserver -> hiển thị luôn (an toàn)
        if (typeof window.IntersectionObserver === 'undefined') {
            this.initPreview();
            return;
        }

        this.observer = new window.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.unwatchPreview();
                    this.initPreview();
                }
            });
        }, { rootMargin: '200px' });

        this.observer.observe(el);
    }

    unwatchPreview() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    destroyPlayer() {
        if (this.player) {
            try {
                this.player.stopAnimation(true);
                this.player.clear();
            } catch (e) {
                // bỏ qua
            }
            this.player = null;
        }
    }

    /** Chỉ tạo player cho file .svga, và chỉ khi ô đã vào viewport. */
    initPreview() {
        if (this.player || !this.previewRef.current) return;
        const url = this.props.url || '';
        if (typeof url !== 'string' || !url.toLowerCase().endsWith('.svga')) return;

        try {
            const parser = new SVGA.Parser();
            parser.load(url, (videoItem) => {
                if (!this.previewRef.current || this.player) return;
                const player = new SVGA.Player(this.previewRef.current);
                player.loops = 0;
                player.clearsAfterStop = false;
                player.setVideoItem(videoItem);
                player.setContentMode(this.props.contentMode || 'AspectFill');
                player.startAnimation();
                this.player = player;
            });
        } catch (e) {
            // url lỗi -> bỏ qua, không làm sập bảng
        }
    }

    render() {
        const { url, width, height } = this.props;
        const src = typeof url === 'string' ? url : '';

        if (src.toLowerCase().endsWith('.svga')) {
            return <div ref={this.previewRef} style={{ width: width, height: height }} />;
        }
        if (!src) return null;
        return (
            <img
                src={src}
                alt=""
                loading="lazy"
                style={{ width: width, height: height, objectFit: 'contain' }}
                onError={(e) => { e.target.onerror = null; e.target.style.visibility = 'hidden'; }}
            />
        );
    }
}

ShopPreview.defaultProps = {
    width: 50,
    height: 50,
};

export default ShopPreview;
